import React, { useEffect, useState } from "react";
import "./FuturisticDashboard.css";
import visitorIcon from "../../../assets/svg/visitor-future.svg";
import vehicleIcon from "../../../assets/svg/vehicle-future.svg";
import {
  getVisitorPredictionApi,
  getVehiclePredictionApi,
  getCourierPredictionApi,
  getVisitorPeakTimeApi,
  getVisitorEntryApi,
  getOverstayApi,
  getDailyAttendanceApi,
  getPurposePredictionApi,
  getRegisterPredictionApi,
  getPersonPredictionApi,
  getGatePredictionApi,
} from "./futuristic-api.js";
const StatCard = ({ title, value, hint }) => (
  <div className="fs-card stat-card">
    <div className="stat-title">{title}</div>
    <div className="stat-number">{value}</div>
    {hint && <div className="stat-hint muted">{hint}</div>}
  </div>
);

const SmallTile = ({ value, text, icon }) => (
  <div className="fs-card small-tile">
    {icon && (
      <div className="small-tile-icon mb-3">
        <img src={icon} alt="" width={64} height={64} />
      </div>
    )}
    <div className="small-number">{value}</div>
    <div className="small-text muted">{text}</div>
  </div>
);

const HeatCell = ({ level, label, onHover }) => (
  <div className={`heatcell ${level}`} title={label} onMouseEnter={onHover} />
);

const Heatmap = ({ peakTimeData }) => {
  const [hoveredCell, setHoveredCell] = React.useState(null);
  const [tooltipPosition, setTooltipPosition] = React.useState({ x: 0, y: 0 });

  // Process API data or use default dates
  const predictions = peakTimeData?.predictions || [];
  const legend = peakTimeData?.legend || { low: 1, moderate: 3, high: 5 };

  // Get today's date for comparison
  const today = new Date().toISOString().split("T")[0];

  // Dates for the week - use API data or fallback
  const dates =
    predictions.length > 0
      ? predictions.map((pred) => {
          const dateObj = new Date(pred.raw_date);
          const day = dateObj.getDate();
          const month = dateObj.toLocaleString("en-US", { month: "short" });
          const isToday = pred.raw_date.startsWith(today);
          return { day: String(day), month, isToday };
        })
      : [
          { day: "", month: "", isToday: false },
          { day: "", month: "", isToday: false },
          { day: "", month: "", isToday: false },
          { day: "", month: "", isToday: false },
          { day: "", month: "", isToday: false },
          { day: "", month: "", isToday: false },
          { day: "", month: "", isToday: false },
        ];

  // Time slots from 9am to 8pm
  const timeSlots = [
    "9am",
    "10am",
    "11am",
    "12pm",
    "1pm",
    "2pm",
    "3pm",
    "4pm",
    "5pm",
    "6pm",
    "7pm",
    "8pm",
  ];

  // Get current time slot index
  const getCurrentTimeSlot = () => {
    const now = new Date();
    const currentHour = now.getHours();
    // Map current hour to time slot index (9am = 0, 8pm = 11)
    if (currentHour >= 9 && currentHour <= 20) {
      return currentHour - 9;
    }
    return -1; // Not in range
  };

  const currentTimeSlotIndex = getCurrentTimeSlot();

  // Map time slots to API hour keys
  const timeSlotToApiKey = {
    "9am": "09:00 am",
    "10am": "10:00 am",
    "11am": "11:00 am",
    "12pm": "12:00 pm",
    "1pm": "01:00 pm",
    "2pm": "02:00 pm",
    "3pm": "03:00 pm",
    "4pm": "04:00 pm",
    "5pm": "05:00 pm",
    "6pm": "06:00 pm",
    "7pm": "07:00 pm",
    "8pm": "08:00 pm",
  };

  // Convert count to level based on legend
  const getLevel = (count) => {
    if (count === 0) return null;
    if (count >= legend.high) return "high";
    if (count >= legend.moderate) return "moderate";
    if (count >= legend.low) return "low";
    return null;
  };

  // Generate heatmap data (7 rows x 12 columns)
  const generateHeatmapData = () => {
    const data = [];

    // If we have API data, use it
    if (predictions.length > 0) {
      predictions.forEach((prediction) => {
        const row = [];
        timeSlots.forEach((timeSlot) => {
          const apiKey = timeSlotToApiKey[timeSlot];
          const count = prediction.hours[apiKey] || 0;
          const level = getLevel(count);

          if (level) {
            row.push({ level, count });
          } else {
            row.push(null);
          }
        });
        console.log("row", row);
        data.push(row);
      });
    } else {
      // Fallback: create empty grid
      dates.forEach(() => {
        data.push(Array(timeSlots.length).fill(null));
      });
    }
    return data;
  };

  const heatmapData = React.useMemo(() => generateHeatmapData(), []);

  const handleCellHover = (rowIdx, colIdx, event) => {
    const cellData = heatmapData[rowIdx][colIdx];
    if (cellData) {
      setHoveredCell({ row: rowIdx, col: colIdx, count: cellData.count });
      const rect = event.currentTarget.getBoundingClientRect();
      setTooltipPosition({
        x: rect.left + rect.width / 2,
        y: rect.top - 10,
      });
    }
  };

  const handleCellLeave = () => {
    setHoveredCell(null);
  };

  return (
    <div className="fs-card heatmap-card">
      <div className="heatmap-header">
        <h3 className="heatmap-title">Most visiting peak times</h3>
        <div className="heatmap-legend">
          <div className="legend-item">
            <span className="legend-dot legend-low"></span>
            <span className="legend-text">Normal</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot legend-moderate"></span>
            <span className="legend-text">Moderate</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot legend-high"></span>
            <span className="legend-text">High</span>
          </div>
        </div>
      </div>

      <div className="heatmap-container">
        <div className="heatmap-dates">
          {dates.map((date, idx) => (
            <div
              key={idx}
              className={`heatmap-date ${date.isToday ? "today" : ""}`}
            >
              {date.day} {date.month}
            </div>
          ))}
        </div>

        <div className="heatmap-grid-wrapper">
          <div className="heatmap-grid">
            {dates.map((date, rowIdx) => (
              <React.Fragment key={rowIdx}>
                {timeSlots.map((time, colIdx) => {
                  const cellData = heatmapData[rowIdx][colIdx];
                  return (
                    <div
                      key={`${rowIdx}-${colIdx}`}
                      className={`heatmap-cell ${
                        cellData ? `heatmap-${cellData.level}` : "heatmap-empty"
                      }`}
                      onMouseEnter={(e) => handleCellHover(rowIdx, colIdx, e)}
                      onMouseLeave={handleCellLeave}
                    />
                  );
                })}
              </React.Fragment>
            ))}
          </div>

          <div className="heatmap-times">
            {timeSlots.map((time, idx) => (
              <div
                key={idx}
                className={`heatmap-time ${
                  idx === currentTimeSlotIndex ? "current-time" : ""
                }`}
              >
                {time}
              </div>
            ))}
          </div>
        </div>
      </div>

      {hoveredCell && (
        <div
          className="heatmap-tooltip"
          style={{
            position: "fixed",
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: "translate(-50%, -100%)",
          }}
        >
          {hoveredCell.count}
        </div>
      )}
    </div>
  );
};

const VisitorsEntryGrid = ({ entryData }) => {
  const [hoveredCell, setHoveredCell] = React.useState(null);
  const [tooltipPosition, setTooltipPosition] = React.useState({ x: 0, y: 0 });

  const predictions = entryData?.predictions || {};
  const legend = entryData?.legend || {
    visitors: { low: 0, moderate: 0, high: 0 },
    vehicles: { low: 0, moderate: 0, high: 0 },
    couriers: { low: 0, moderate: 0, high: 0 },
  };

  const today = new Date().toISOString().split("T")[0];

  // Date range for the week - use API data or fallback
  const dates =
    predictions.visitors?.length > 0
      ? predictions.visitors.map((pred) => {
          const dateObj = new Date(pred.raw_date);
          const day = dateObj.getDate();
          const month = dateObj.toLocaleString("en-US", { month: "short" });
          const isToday = pred.raw_date.startsWith(today);
          return { day: String(day), month, isToday, label: pred.date };
        })
      : [
          { day: "", month: "", isToday: false },
          { day: "", month: "", isToday: false },
          { day: "", month: "", isToday: false },
          { day: "", month: "", isToday: false },
          { day: "", month: "", isToday: false },
          { day: "", month: "", isToday: false },
          { day: "", month: "", isToday: false },
        ];

  // Categories to display
  const categories = ["Visitors", "Vehicles", "Couriers"];

  // Convert count to level based on legend
  const getLevel = (category, count) => {
    const categoryKey = category.toLowerCase();
    console.log(categoryKey, count);

    const thresholds = legend[categoryKey];
    console.log("thresholds", thresholds);
    if (!thresholds || count === 0) return "normal";

    if (count >= thresholds.high) return "high";
    if (count >= thresholds.moderate) return "moderate";
    if (count >= thresholds.low) return "normal";
    return "normal";
  };

  // Generate pattern data from API or use fallback
  const generatePattern = () => {
    if (predictions.visitors?.length > 0) {
      return {
        Visitors: predictions.visitors.map((pred) => ({
          level: getLevel("visitors", pred.count),
          count: pred.count,
        })),
        Vehicles:
          predictions.vehicles?.map((pred) => ({
            level: getLevel("vehicles", pred.count),
            count: pred.count,
          })) || [],
        Couriers:
          predictions.couriers?.map((pred) => ({
            level: getLevel("couriers", pred.count),
            count: pred.count,
          })) || [],
      };
    }

    // Fallback pattern
    return {
      Visitors: Array(7).fill({ level: "normal", count: 0 }),
      Vehicles: Array(7).fill({ level: "normal", count: 0 }),
      Couriers: Array(7).fill({ level: "normal", count: 0 }),
    };
  };

  const patternData = generatePattern();
  console.log("patternData", patternData);
  // Calculate total visitors count
  const totalVisitors =
    predictions.visitors?.reduce((sum, pred) => sum + pred.count, 0) || 4672;

  // Get date range
  const firstDate = dates[0];
  const lastDate = dates[dates.length - 1];

  return (
    <div className="fs-card visitors-entry-card">
      {/* Header Section */}
      <div className="visitors-entry-header">
        <div className="visitors-entry-title-section">
          <h3 className="visitors-entry-title">Visitors Entry</h3>
          <p className="visitors-entry-subtitle">Predicted data by past data</p>
        </div>
        <div className="visitors-entry-legend">
          <div className="visitors-legend-item">
            <span className="visitors-legend-dot visitors-legend-normal"></span>
            <span className="visitors-legend-text">Normal</span>
          </div>
          <div className="visitors-legend-item">
            <span className="visitors-legend-dot visitors-legend-moderate"></span>
            <span className="visitors-legend-text">Moderate</span>
          </div>
          <div className="visitors-legend-item">
            <span className="visitors-legend-dot visitors-legend-high"></span>
            <span className="visitors-legend-text">High</span>
          </div>
        </div>
      </div>

      {/* Visitors Count */}
      <div className="visitors-entry-count">
        <span className="visitors-count-number">
          {totalVisitors.toLocaleString()}
        </span>
        <span className="visitors-count-label">visitors</span>
      </div>

      {/* Date Range */}
      <div className="visitors-entry-daterange">
        From{" "}
        <strong>
          {firstDate.day} {firstDate.month}, 2026
        </strong>{" "}
        -{" "}
        <strong>
          {lastDate.day} {lastDate.month}, 2026
        </strong>
      </div>

      {/* Grid Section */}
      <div className="visitors-entry-grid">
        {/* Category Labels Column */}
        <div className="visitors-grid-labels">
          {categories.map((category, idx) => (
            <div key={idx} className="visitors-grid-label">
              {category}
            </div>
          ))}
          <div className="visitors-grid-label-spacer"></div>
        </div>

        {/* Date Columns */}
        <div className="visitors-grid-content">
          {/* Data Grid */}
          {categories.map((category, catIdx) => (
            <div key={catIdx} className="visitors-grid-row">
              {dates.map((date, dateIdx) => {
                const cellData = patternData[category][dateIdx];
                const level = cellData?.level || "normal";
                const count = cellData?.count || 0;
                return (
                  <div
                    key={dateIdx}
                    className="visitors-grid-cell"
                    onMouseEnter={(e) => {
                      setHoveredCell({ category, date: date.label, count });
                      const rect = e.currentTarget.getBoundingClientRect();
                      setTooltipPosition({
                        x: rect.left + rect.width / 2,
                        y: rect.top - 10,
                      });
                    }}
                    onMouseLeave={() => setHoveredCell(null)}
                  >
                    <div
                      className={`visitors-status-circle visitors-status-${level}`}
                    ></div>
                  </div>
                );
              })}
            </div>
          ))}
          {/* Date Headers */}
          <div className="visitors-grid-dates">
            {dates.map((date, idx) => (
              <div
                key={idx}
                className={`visitors-grid-date ${date.isToday ? "today" : ""}`}
              >
                <span className="date-day">
                  {date.day} {date.month}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tooltip */}
      {hoveredCell && (
        <div
          className="heatmap-tooltip"
          style={{
            position: "fixed",
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: "translate(-50%, -100%)",
            zIndex: 1000,
          }}
        >
          {hoveredCell.count}
        </div>
      )}
    </div>
  );
};

const BubbleGrid = () => (
  <div className="fs-card bubble-card">
    <div className="bubble-head">Visitors Entry</div>
    <div className="bubble-grid">
      {Array.from({ length: 21 }).map((_, i) => (
        <div key={i} className={`bubble ${i % 6 === 0 ? "big" : ""}`}>
          {/* decorative */}
        </div>
      ))}
    </div>
  </div>
);

const BarChart = ({ attendanceApiData }) => {
  const [tooltip, setTooltip] = React.useState(null);
  const [tooltipPosition, setTooltipPosition] = React.useState({ x: 0, y: 50 });

  const chartData = attendanceApiData?.chart_data || [];
  const percentageChange = attendanceApiData?.percentage_change || 43;
  const isIncreased = attendanceApiData?.is_increased ?? true;

  const today = new Date().toISOString().split("T")[0];

  // Transform API data to component format
  const attendanceData =
    chartData.length > 0
      ? chartData.map((item) => ({
          date: item.date,
          visitor: item.type.Visitor || 0,
          vehicle: item.type.Vehicle || 0,
          courier: item.type.Courier || 0,
          total: item.total,
          isToday: item.raw_date === today,
        }))
      : [
          {
            date: "23 Feb",
            visitor: 100,
            vehicle: 80,
            courier: 50,
            isToday: false,
          },
          {
            date: "24 Feb",
            visitor: 75,
            vehicle: 60,
            courier: 38,
            isToday: false,
          },
          {
            date: "25 Feb",
            visitor: 95,
            vehicle: 70,
            courier: 45,
            isToday: false,
          },
          {
            date: "26 Feb",
            visitor: 60,
            vehicle: 45,
            courier: 30,
            isToday: false,
          },
          {
            date: "27 Feb",
            visitor: 100,
            vehicle: 85,
            courier: 55,
            isToday: true,
          },
          {
            date: "28 Feb",
            visitor: 85,
            vehicle: 65,
            courier: 42,
            isToday: false,
          },
          {
            date: "29 Feb",
            visitor: 90,
            vehicle: 68,
            courier: 48,
            isToday: false,
          },
        ];

  const maxValue = Math.max(
    ...attendanceData.map((d) => d.visitor + d.vehicle + d.courier)
  );

  const handleBarHover = (data, event) => {
    const total = data.visitor + data.vehicle + data.courier;
    setTooltip({
      total,
      visitor: data.visitor,
      vehicle: data.vehicle,
      courier: data.courier,
    });

    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    });
  };

  const handleBarLeave = () => {
    setTooltip(null);
  };

  return (
    <div className="fs-card attendance-card">
      <div className="attendance-head">Daily Attendance</div>
      <div className="attendance-number">
        <span className="accent">
          {isIncreased ? "+" : "-"}
          {percentageChange}%
        </span>{" "}
        will {isIncreased ? "increased" : "decreased"} from present
      </div>
      <div className="bar-series">
        {attendanceData.map((data, i) => {
          const total = data.visitor + data.vehicle + data.courier;
          const visitorHeight = (data.visitor / 100) * 100;
          const vehicleHeight = (data.vehicle / 100) * 100;
          const courierHeight = (data.courier / 100) * 100;

          return (
            <div key={i} className="bar-group">
              <div
                className="bar-columns"
                onMouseEnter={(e) => handleBarHover(data, e)}
                onMouseLeave={handleBarLeave}
              >
                <div
                  className="bar-fill visitor-bar"
                  style={{ height: `${visitorHeight}%` }}
                />
                <div
                  className="bar-fill vehicle-bar"
                  style={{ height: `${vehicleHeight}%` }}
                />
                <div
                  className="bar-fill courier-bar"
                  style={{ height: `${courierHeight}%` }}
                />
              </div>
              <div
                className={`attendance-bar-label ${
                  data.isToday ? "today" : ""
                }`}
              >
                {data.date}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="attendance-legend">
        <div className="legend-item">
          <span className="legend-dot visitor-dot"></span>
          <span className="legend-text">Visitor</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot vehicle-dot"></span>
          <span className="legend-text">Vehicle</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot courier-dot"></span>
          <span className="legend-text">Courier</span>
        </div>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="attendance-tooltip"
          style={{
            position: "fixed",
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: "translate(-50%, -100%)",
            zIndex: 1000,
          }}
        >
          <div className="tooltip-total">{tooltip.total}</div>
          <div className="tooltip-breakdown">
            <div className="tooltip-row">
              <span className="tooltip-dot visitor-dot"></span>
              <span className="tooltip-label">Visitor</span>
              <span className="tooltip-colon">:</span>
              <span className="tooltip-value">{tooltip.visitor}</span>
            </div>
            <div className="tooltip-row">
              <span className="tooltip-dot vehicle-dot"></span>
              <span className="tooltip-label">Vehicle</span>
              <span className="tooltip-colon">:</span>
              <span className="tooltip-value">{tooltip.vehicle}</span>
            </div>
            <div className="tooltip-row">
              <span className="tooltip-dot courier-dot"></span>
              <span className="tooltip-label">Courier</span>
              <span className="tooltip-colon">:</span>
              <span className="tooltip-value">{tooltip.courier}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PredictionCard = ({
  title,
  value,
  dataPoints,
  diffPercent,
  diffCount,
  peakLabel,
  peakTime,
  gradientId,
}) => {
  const maxValue = Math.max(...dataPoints);
  const width = 560;
  const height = 74;
  const padding = 10;

  // Generate SVG path for smooth curve
  const generatePath = () => {
    const points = dataPoints.map((value, index) => {
      const x =
        (index / (dataPoints.length - 1)) * (width - 2 * padding) + padding;
      const y = height - (value / maxValue) * (height - 2 * padding) - padding;
      return { x, y };
    });

    let path = `M ${points[0].x} ${points[0].y}`;
    for (let i = 0; i < points.length - 1; i++) {
      const xMid = (points[i].x + points[i + 1].x) / 2;
      const yMid = (points[i].y + points[i + 1].y) / 2;
      const cpX = (xMid + points[i].x) / 2;
      const cpY = points[i].y;
      path += ` Q ${cpX} ${cpY}, ${xMid} ${yMid}`;
      const cpX2 = (xMid + points[i + 1].x) / 2;
      const cpY2 = points[i + 1].y;
      path += ` Q ${cpX2} ${cpY2}, ${points[i + 1].x} ${points[i + 1].y}`;
    }
    return path;
  };

  const linePath = generatePath();
  const areaPath = `${linePath} L ${
    width - padding
  } ${height} L ${padding} ${height} Z`;

  return (
    <div className="fs-card visitors-prediction-card">
      <div className="visitors-prediction-title">{title}</div>
      <div className="visitors-prediction-value">{value}</div>

      <div className="visitors-chart-container">
        <svg
          width="100%"
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop
                offset="0%"
                style={{ stopColor: "#a7dbb8", stopOpacity: 0.4 }}
              />
              <stop
                offset="100%"
                style={{ stopColor: "#a7dbb8", stopOpacity: 0.05 }}
              />
            </linearGradient>
          </defs>
          <path d={areaPath} fill={`url(#${gradientId})`} />
          <path
            d={linePath}
            fill="none"
            stroke="#4a9d6a"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </svg>
      </div>

      <div className="visitors-stats-section">
        <div className="visitors-difference">
          <div className="difference-label">Difference from present</div>
          <div className="difference-values">
            <span className="difference-percent">{diffPercent}</span>
            <div className="difference-count">
              <span className="difference-arrow">↑</span>
              <span>{diffCount}</span>
            </div>
          </div>
        </div>

        <div className="visitors-peak">
          <div className="peak-label">{peakLabel}</div>
          <div className="peak-time">{peakTime}</div>
        </div>
      </div>
    </div>
  );
};

const VisitorsPrediction = ({ predictionData }) => {
  const {
    prediction = 0,
    diff_percent = 0,
    diff_value = 0,
    is_decreased = false,
    peak_hour = "09:00 AM - 01:00 PM hrs",
    today_actual = 0,
  } = predictionData || {};

  return (
    <PredictionCard
      title="Visitors Prediction"
      value={prediction.toLocaleString()}
      dataPoints={[85, 45, 65, 72, 50, 35, 58, 75, 82, 68, 50, 75]}
      diffPercent={`${is_decreased ? "-" : "+"}${diff_percent}%`}
      diffCount={diff_value.toString()}
      peakLabel="Peak visitors hours"
      peakTime={peak_hour}
      gradientId="areaGradient"
    />
  );
};

const VehiclesPrediction = ({ predictionData }) => {
  const {
    prediction = 0,
    diff_percent = 0,
    diff_value = 0,
    is_decreased = false,
    peak_hour = "09:00 AM - 01:00 PM hrs",
    today_actual = 0,
  } = predictionData || {};

  return (
    <PredictionCard
      title="Vehicles Prediction"
      value={prediction.toLocaleString()}
      dataPoints={[70, 55, 80, 65, 45, 60, 75, 85, 70, 55, 65, 80]}
      diffPercent={`${is_decreased ? "-" : "+"}${diff_percent}%`}
      diffCount={diff_value.toString()}
      peakLabel="Peak vehicles hours"
      peakTime={peak_hour}
      gradientId="vehicleGradient"
    />
  );
};

const CourierPrediction = ({ predictionData }) => {
  const {
    prediction = 0,
    diff_percent = 0,
    diff_value = 0,
    is_decreased = false,
    peak_hour = "09:00 AM - 01:00 PM hrs",
    today_actual = 0,
  } = predictionData || {};

  return (
    <PredictionCard
      title="Courier Prediction"
      value={prediction.toLocaleString()}
      dataPoints={[50, 35, 55, 45, 30, 40, 60, 55, 45, 35, 50, 65]}
      diffPercent={`${is_decreased ? "-" : "+"}${diff_percent}%`}
      diffCount={diff_value.toString()}
      peakLabel="Peak courier hours"
      peakTime={peak_hour}
      gradientId="courierGradient"
    />
  );
};

const PurposePrediction = ({ purposeApiData }) => {
  const purposes = purposeApiData?.purposes || [];
  const percentageChange = purposeApiData?.percentage_change || 73.6;
  const isIncreased = purposeApiData?.is_increased ?? true;

  // Define color palette
  const colorPalette = ["#6b7280", "#7c8db0", "#86d6a3", "#92d4f0", "#c5b8e8"];

  // Transform API data to component format
  const purposeData =
    purposes.length > 0
      ? purposes.map((purpose, index) => ({
          value: purpose.count,
          label: purpose.name,
          color: colorPalette[index % colorPalette.length],
        }))
      : [];

  const maxValue = Math.max(...purposeData.map((d) => d.value), 1);

  return (
    <div className="fs-card purpose-card">
      <div className="col-12 col-md-6 purpose-header">
        <h3 className="purpose-head">Purpose based prediction</h3>
        <div className="purpose-title-section">
          <div className="purpose-timeframe">
            <span className="timeframe-badge">7 days</span>
          </div>
          <div className="purpose-stats">
            <span className="stats-increase">
              {isIncreased ? "+" : "-"}
              {percentageChange}%
            </span>
            <span className="stats-text">better than present statics</span>
          </div>
        </div>
        <div className="purpose-legend ">
          {purposeData.map((item, index) => (
            <div key={index} className="legend-item">
              <span
                className="legend-dot"
                style={{ backgroundColor: item.color }}
              />
              <span className="legend-label">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="col-12 col-md-6  purpose-chart">
        {purposeData.map((item, index) => {
          const heightPercent = (item.value / maxValue) * 100;
          return (
            <div key={index} className="purpose-bar-container">
              <div
                className="purpose-pill"
                style={{
                  height: `${heightPercent > 10 ? heightPercent : 15}%`,
                  background: `linear-gradient(180deg, ${item.color}, ${item.color}dd)`,
                }}
              >
                <span className="purpose-pill-value">{item.value}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PersonItem = ({ person, colorIndex }) => (
  <div className="person-item">
    <div className="person-avatar">
      <div className={`avatar avatar-${colorIndex}`}>
        {person.name
          .split(" ")
          .map((n) => n[0])
          .slice(0, 2)
          .join("")}
      </div>
    </div>
    <div className="person-info">
      <div className="person-name">{person.name}</div>
      <div className="person-role muted">{person.role}</div>
    </div>
    <div className="person-middle">
      <div className="person-count">{person.count}</div>
    </div>
    <div className={`person-badge badge-${colorIndex}`}>
      <div className="badge-left">
        <span className="badge-percent">{person.percent}%</span>
      </div>
      <div className="badge-right">
        <span className="badge-text">
          will be increased compared to present
        </span>
      </div>
    </div>
  </div>
);

const PersonList = ({ personApiData = [] }) => {
  const colorPalette = ["#F8DAD9", "#FCEFD7", "#EDE6FF", "#DDEEFF", "#E7E7E7"];

  const people =
    personApiData.length > 0
      ? personApiData.map((person, index) => ({
          id: index + 1,
          name: person.name ?? "Unknown",
          role: person.mobile_number ?? "N/A",
          count: person.predicted_count ?? 0,
          percent:
            person.percentage_change >= 100
              ? 100
              : person.percentage_change ?? 0,
          is_increased: person.is_increased ?? true,
          color: colorPalette[index % colorPalette.length],
        }))
      : [];
  return (
    <div className="person-card">
      <h3 className="person-title">Person based visitors prediction</h3>
      <div className="person-list">
        {people.map((p) => (
          <div key={p.id} className="person-row">
            <div className="person-row-flex px-3 py-2">
              <div className="person-left">
                <img
                  className="person-avatar"
                  src={`https://i.pravatar.cc/48?img=${p.id}`}
                  alt={`Avatar of ${p.name}`}
                />
                <div className="person-meta">
                  <div className="person-name">{p.name}</div>
                  <div className="person-role">{p.role}</div>
                </div>
              </div>
              <div className="person-count">{p.count}</div>
            </div>

            <div className="person-progressWrap">
              <div
                className="person-progressBg"
                /*  style={{ background: p.color }} */
                aria-hidden
              >
                <div
                  className="person-progressFill"
                  style={{
                    width: `${
                      p.percent < 30
                        ? 40 + p.percent
                        : p.percent < 60
                        ? 30 + p.percent
                        : p.percent
                    }%`,
                    background: `${p.color}`,
                  }}
                />
                <div className="person-progressText">
                  <span className="person-percent">
                    {p.is_increased ? "+" : "-"}
                    {p.percent}%
                  </span>
                  <span className="person-note">
                    will be {p.is_increased ? "increased" : "decreased"} <br />{" "}
                    compared to present
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const RegistrationPrediction = ({ registrationApiData = {} }) => {
  const totalSegments = 30;
  const percentage = registrationApiData?.percentage_change ?? 43;
  const filledSegments = Math.round((percentage / 100) * totalSegments);
  const registrations = registrationApiData?.registrations ?? [
    { label: "Pre - Register", count: 808 },
    { label: "Direct Scheduling", count: 308 },
  ];
  const isIncreased = registrationApiData?.is_increased ?? true;

  return (
    <div className="fs-card registration-card">
      <div className="registration-head">Registration Prediction</div>
      <div className="reg-body">
        <div className="reg-gauge">
          <div className="gauge-ring">
            <svg width="280" height="160" viewBox="0 0 280 160">
              {Array.from({ length: totalSegments }).map((_, i) => {
                // Start from left (-180 degrees) to right (0 degrees)
                const angle = -180 + (180 / (totalSegments - 1)) * i;
                const radius = 120;
                const segmentLength = 22;
                const segmentWidth = 10;

                const x1 = 140 + radius * Math.cos((angle * Math.PI) / 180);
                const y1 = 140 + radius * Math.sin((angle * Math.PI) / 180);
                const x2 =
                  140 +
                  (radius - segmentLength) * Math.cos((angle * Math.PI) / 180);
                const y2 =
                  140 +
                  (radius - segmentLength) * Math.sin((angle * Math.PI) / 180);

                // Create gradient effect from dark green to light green
                let color;
                if (i < filledSegments) {
                  // Dark green to medium green gradient
                  const ratio = i / filledSegments;
                  if (ratio < 0.5) {
                    color = "#10b981"; // Dark green
                  } else if (ratio < 0.75) {
                    color = "#34d399"; // Medium green
                  } else {
                    color = "#6ee7b7"; // Medium-light green
                  }
                } else {
                  // Light green for unfilled segments
                  color = "#d1fae5";
                }

                return (
                  <line
                    key={i}
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    stroke={color}
                    strokeWidth={segmentWidth}
                    strokeLinecap="round"
                  />
                );
              })}
            </svg>
            <div className="gauge-center">
              <div className="gauge-percent">
                {isIncreased ? "+" : "-"}
                {percentage}%
              </div>
              <div className="gauge-text">
                will {isIncreased ? "increased" : "decreased"} from present
              </div>
            </div>
          </div>
        </div>

        <div className="reg-cards">
          {registrations.map((reg, index) => (
            <div className="reg-card" key={index}>
              <div className="reg-label">{reg.label}</div>
              <div className="reg-number">{reg.count}</div>
              <div className="reg-subtext">will be recorded in this period</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const GatePrediction = ({ gateApiData = {} }) => {
  const defaultGates = [
    {
      gatename: "Front gate - Main",
      count: 2067,
      percentage_change: 45,
      is_increased: true,
    },
    {
      gatename: "South gate",
      count: 1560,
      percentage_change: 30,
      is_increased: true,
    },
    {
      gatename: "South gate",
      count: 1264,
      percentage_change: 68,
      is_increased: true,
    },
    {
      gatename: "Main gate",
      count: 1967,
      percentage_change: 76,
      is_increased: true,
    },
  ];

  const gates =
    gateApiData?.gates?.length > 0 ? gateApiData.gates : defaultGates;
  const percentageChange = gateApiData?.percentage_change ?? 0;
  const isIncreased = gateApiData?.is_increased ?? true;

  // Circle properties for up to 4 gates
  const circleConfigs = [
    {
      cy: 150,
      r: 140,
      fill: "rgba(209, 250, 229, 0.3)",
      labelY: 35,
      valueY: 55,
      labelClass: "gate-label",
      valueClass: "gate-value",
      lineY: 40,
      lineX1: 290,
    },
    {
      cy: 180,
      r: 110,
      fill: "rgba(167, 243, 208, 0.4)",
      labelY: 95,
      valueY: 115,
      labelClass: "gate-label",
      valueClass: "gate-value",
      lineY: 105,
      lineX1: 260,
    },
    {
      cy: 210,
      r: 80,
      fill: "rgba(110, 231, 183, 0.5)",
      labelY: 160,
      valueY: 180,
      labelClass: "gate-label",
      valueClass: "gate-value",
      lineY: 165,
      lineX1: 230,
    },
    {
      cy: 240,
      r: 50,
      fill: "rgba(16, 185, 129, 0.9)",
      labelY: 235,
      valueY: 255,
      labelClass: "gate-label-white",
      valueClass: "gate-value-white",
      lineY: 225,
      lineX1: 200,
    },
  ];

  return (
    <div className="fs-card futuristic-gate-card">
      <div className="gate-head">Gate based visitors prediction</div>
      <div className="gate-body">
        <div className="gate-circles-container">
          <svg
            className="gate-svg"
            viewBox="0 0 400 310"
            width="400"
            height="310"
          >
            {gates.slice(0, 4).map((gate, index) => {
              const config = circleConfigs[index];
              return (
                <React.Fragment key={index}>
                  <circle
                    cx="150"
                    cy={config.cy}
                    r={config.r}
                    fill={config.fill}
                  />
                  <text
                    x="150"
                    y={config.labelY}
                    textAnchor="middle"
                    className={config.labelClass}
                  >
                    {gate.gatename}
                  </text>
                  <text
                    x="150"
                    y={config.valueY}
                    textAnchor="middle"
                    className={config.valueClass}
                  >
                    {gate.count}
                  </text>

                  {/* Dotted line and percentage */}
                  <line
                    x1={config.lineX1}
                    y1={config.lineY}
                    x2="310"
                    y2={config.lineY}
                    stroke="#9ca3af"
                    strokeWidth="2"
                    strokeDasharray="4,4"
                  />
                  <text
                    x="330"
                    y={config.lineY - 5}
                    className="gate-percent-label"
                  >
                    {gate.is_increased ? "+" : "-"}
                    {gate.percentage_change}%
                  </text>
                  <text
                    x="330"
                    y={config.lineY + 10}
                    className="gate-percent-sub"
                  >
                    will be {gate.is_increased ? "increased" : "decreased"}
                  </text>
                </React.Fragment>
              );
            })}
          </svg>
        </div>
      </div>
      <div className="gate-footer">
        <span className="info-icon">ⓘ</span>
        <span className="gate-footer-text">
          These results and counts are predictive values. Please review them
          carefully.
        </span>
      </div>
    </div>
  );
};

export default function FuturisticDashboard() {
  const [visitorPrediction, setVisitorPrediction] = useState({});
  const [vehiclePrediction, setVehiclePrediction] = useState({});
  const [courierPrediction, setCourierPrediction] = useState({});
  const [visitorPeakTime, setVisitorPeakTime] = useState({});
  const [visitorEntry, setVisitorEntry] = useState({});
  const [overstay, setOverstay] = useState({
    visitors_count: 0,
    visitors_percent: 0,
    vehicles_count: 0,
    vehicles_percent: 0,
  });
  const [dailyAttendance, setDailyAttendance] = useState({});
  const [purposePrediction, setPurposePrediction] = useState({});
  const [registrationPrediction, setRegistrationPrediction] = useState({});
  const [personPrediction, setPersonPrediction] = useState([]);
  const [gatePrediction, setGatePrediction] = useState({});

  const handleDownloadPDF = async () => {
    try {
      const html2canvas = (await import("html2canvas")).default;
      const jsPDF = (await import("jspdf")).default;

      const element = document.querySelector(".featurestic-dashboard");
      if (!element) return;

      // Capture the dashboard as canvas
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      // Add first page
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if content is longer
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Generate filename with current date
      const date = new Date().toISOString().split("T")[0];
      pdf.save(`futuristic-dashboard-${date}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(
        "Failed to generate PDF. Please ensure html2canvas and jspdf are installed."
      );
    }
  };

  const handlePrint = async () => {
    try {
      const html2canvas = (await import("html2canvas")).default;

      const element = document.querySelector(".featurestic-dashboard");
      if (!element) return;

      // Capture the dashboard as canvas with styles
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      // Create a new window for printing
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        alert("Please allow popups to print");
        return;
      }

      const imgData = canvas.toDataURL("image/png");

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Futuristic Dashboard - ${new Date().toLocaleDateString()}</title>
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body { margin: 0; padding: 0; }
              img { 
                width: 100%; 
                height: auto; 
                display: block;
                page-break-inside: avoid;
              }
              @media print {
                body { margin: 0; }
                img { page-break-inside: avoid; }
              }
            </style>
          </head>
          <body>
            <img src="${imgData}" alt="Dashboard" />
          </body>
        </html>
      `);

      printWindow.document.close();

      // Wait for image to load before printing
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 250);
      };
    } catch (error) {
      console.error("Error printing:", error);
      alert("Failed to print. Please try again.");
    }
  };

  useEffect(() => {
    getVisitorPrediction();
    getVehiclePrediction();
    getCourierPrediction();
    getVisitorPeakTime();
    getVisitorEntry();
    getOverstay();
    getDailyAttendance();
    getPurposePrediction();
    getRegistrationPrediction();
    getPersonPrediction();
    getGatePrediction();
  }, []);

  const getVisitorPrediction = async () => {
    try {
      const response = await getVisitorPredictionApi("");

      if (response.data.success === true) {
        setVisitorPrediction(response.data.data ?? {});
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching category:", err);
    } finally {
    }
  };
  const getVehiclePrediction = async () => {
    try {
      const response = await getVehiclePredictionApi("");

      if (response.data.success === true) {
        setVehiclePrediction(response.data.data ?? {});
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching category:", err);
    } finally {
    }
  };

  const getCourierPrediction = async () => {
    try {
      const response = await getCourierPredictionApi("");

      if (response.data.success === true) {
        setCourierPrediction(response.data.data ?? {});
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching category:", err);
    } finally {
    }
  };

  const getVisitorPeakTime = async () => {
    try {
      const response = await getVisitorPeakTimeApi("");

      if (response.data.success === true) {
        setVisitorPeakTime(response.data.data ?? {});
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching peak time:", err);
    } finally {
    }
  };
  const getVisitorEntry = async () => {
    try {
      const response = await getVisitorEntryApi();

      if (response.data.success === true) {
        setVisitorEntry(response.data.data ?? {});
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching visitor entry:", err);
    } finally {
    }
  };
  const getOverstay = async () => {
    try {
      const response = await getOverstayApi();
      if (response.data.success === true) {
        setOverstay(response.data.data ?? {});
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching visitor entry:", err);
    } finally {
    }
  };

  const getDailyAttendance = async () => {
    try {
      const response = await getDailyAttendanceApi();
      if (response.data.success === true) {
        setDailyAttendance(response.data.data ?? {});
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching daily attendance:", err);
    } finally {
    }
  };

  const getPurposePrediction = async () => {
    try {
      const response = await getPurposePredictionApi();
      if (response.data.success === true) {
        setPurposePrediction(response.data.data ?? {});
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching purpose prediction:", err);
    } finally {
    }
  };

  const getRegistrationPrediction = async () => {
    try {
      const response = await getRegisterPredictionApi();
      if (response.data.success === true) {
        setRegistrationPrediction(response.data.data ?? {});
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching registration prediction:", err);
    } finally {
    }
  };

  const getPersonPrediction = async () => {
    try {
      const response = await getPersonPredictionApi();
      if (response.data.success === true) {
        setPersonPrediction(response.data.data ?? []);
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching person prediction:", err);
    } finally {
    }
  };

  const getGatePrediction = async () => {
    try {
      const response = await getGatePredictionApi();
      if (response.data.success === true) {
        setGatePrediction(response.data.data ?? {});
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching gate prediction:", err);
    } finally {
    }
  };

  return (
    <div className="featurestic-dashboard">
      <div className="top-controls">
        <div className="toggle">
          <button className="btn">Default</button>
          <button className="btn primary">Futuristic</button>
        </div>
        <div className="date-actions">
          <button className="btn" onClick={handleDownloadPDF}>
            Download report
          </button>
          <button className="btn" onClick={handlePrint}>
            Print report
          </button>
          <button className="btn">Show by date</button>
        </div>
      </div>

      <div className="top-row">
        <div className="left-stats">
          <VisitorsPrediction predictionData={visitorPrediction} />
          <VehiclesPrediction predictionData={vehiclePrediction} />
          <CourierPrediction predictionData={courierPrediction} />
        </div>

        <div className="right-heat">
          <Heatmap peakTimeData={visitorPeakTime} />
        </div>
      </div>

      <div className="middle-row">
        <div className="small-tiles">
          <SmallTile
            value={`${overstay.vehicles_count}%`}
            text="Vehicles likely to overstay"
            icon={vehicleIcon}
          />
          <SmallTile
            value={`${overstay.visitors_count ?? 0}%`}
            text="Visitors likely to overstay"
            icon={visitorIcon}
          />
          <SmallTile
            value={`${overstay.vehicles_percent ?? 0}`}
            text="Predicted overstayed vehicle count"
          />
          <SmallTile
            value={`${overstay.visitors_percent ?? 0}`}
            text="Predicted overstayed visitor count"
          />
        </div>

        <div className="bubble-area">
          <VisitorsEntryGrid entryData={visitorEntry} />
        </div>

        <div className="attendance-area">
          <BarChart attendanceApiData={dailyAttendance} />
        </div>
      </div>

      <div className="lower-row">
        <div className="left-col">
          <PurposePrediction purposeApiData={purposePrediction} />
        </div>
        <div className="right-col">
          <RegistrationPrediction
            registrationApiData={registrationPrediction}
          />
        </div>
      </div>
      <div className="lower-row">
        <div className="col-12 col-md-7">
          <PersonList personApiData={personPrediction} />
        </div>
        <div className="col-12 col-md-5">
          <GatePrediction gateApiData={gatePrediction} />
        </div>
      </div>
    </div>
  );
}
